import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs/index';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  BsModalConfirmComponent,
  ConfirmDialogResult,
  createConfirmModalOptions,
  createConfirmRetryModalOptions
} from '../../core/modal/modal-confirm.component';
import { User } from '../../core/model/user.model';
import { AutoremindTranslateService } from '../../core/services/autoremind-translate.service';
import { AuthenticationService } from '../authentication.service';
import { AuthenticationActions } from './authentication.actions';
import { AuthenticationSelectors } from './authentication.selectors';

@Injectable()
export class AuthenticationEffects {
    constructor(
        private actions$: Actions,
        private router: Router,
        private modalService: BsModalService,
        private authenticationService: AuthenticationService,
        private authenticationSelectors: AuthenticationSelectors,
        private autoremindTranslateService: AutoremindTranslateService,
        private location: Location) {
    }

    loginUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.loginUser),
            switchMap((action: any) => {
                // To reuse same viewSessionId we use window.name (session storage is cloned on window.open and can't be used)
                let viewSessionId = window.name;
                if (viewSessionId === '') {
                    viewSessionId = undefined;
                }
                console.log('getting window.name', viewSessionId);
                if (viewSessionId === null) {
                    viewSessionId = undefined;
                }

                return this.authenticationService.loginUser(viewSessionId, action.username, action.password,
                  action.rememberMe, action.accountAccessToken)
                    .pipe(
                        map((response: any) => {

                           const isInIframe = window.self !== window.top;
                           if (!isInIframe) {
                              if (action.rememberMe) {
                                 localStorage.setItem('username', action.username);
                                 localStorage.setItem('password', action.password);
                              } else {
                                 localStorage.removeItem('username');
                                 localStorage.removeItem('password');
                              }
                              localStorage.setItem('rememberMe', action.rememberMe);
                           }
                           if (!viewSessionId) {
                                viewSessionId = response.headers.get('view-session-id');
                                console.log('setting window.name ' + window.name + '=>' + viewSessionId);
                                window.name = viewSessionId;
                            } else {
                                const viewSessionIdFromHeader = response.headers.get('view-session-id');
                                if (viewSessionIdFromHeader !== viewSessionId) {
                                    console.log('viewSessionId changed!!! This should never happen');
                                    console.log('setting window.name ' + window.name + '=>' + viewSessionId);
                                    window.name = viewSessionId;
                                }
                            }

                           return AuthenticationActions
                                .loginUserSuccess({
                                    user: response.body.user,
                                    returnUrl: action.returnUrl,
                                    viewSessionId: response.headers.get('view-session-id'),
                                    portalVersion: response.body.portalVersion,
                                    isAuthenticatedFromAdmin: response.body.user.isAuthenticatedFromAdmin, // action.isAuthenticatedFromAdmin,
                                    returnUrlPathParams: action.returnUrlPathParams
                                });
                        }),
                        catchError(error =>
                            of(AuthenticationActions.loginUserFail())
                        )
                    );
            })
        )
    );

    loginUserSuccess: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.loginUserSuccess),
            tap((action: any) => {
                const user = action.user as User;
                this.autoremindTranslateService.loadTranslationByPermission(user);

                console.log('action.returnUrl :::: ' , action.returnUrl);
                this.router.navigate([action.returnUrl || ''], {queryParams: action.returnUrlPathParams});
            })
        ),
        { dispatch: false }
    );

    logoutUser$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.logoutUser),
            withLatestFrom(this.authenticationSelectors.selectAuthenticate()),
            switchMap(([action, authenticate]) =>
                this.authenticationService.logoutUser(authenticate.viewSessionId)
                    .pipe(
                        switchMap((response: any) => [
                            AuthenticationActions.logoutUserSuccess(),
                            AuthenticationActions.resetFailedRequest()
                        ]), // TODO: Clear window.name (viewSessionId)?
                        catchError(error =>
                            // Even in case of error logoutUser Successfully
                            of(AuthenticationActions.logoutUserSuccess())
                        )
                    )
            )
        )
    );

    windowBeforeUnload$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.windowBeforeUnload),
            withLatestFrom(this.authenticationSelectors.selectAuthenticate()),
            filter(([action, authenticate]) => authenticate.isUserAuthenticated),
            switchMap(([action, authenticate]) =>
                this.authenticationService.logoutUser(authenticate.viewSessionId)
                    .pipe(
                        switchMap((response: any) => [
                            AuthenticationActions.logoutUserSuccess()
                        ]),
                        catchError(error =>
                            // Even in case of error logoutUser Successfully
                            of(AuthenticationActions.logoutUserSuccess())
                        )
                    )
            )
        )
    );

    logoutUserSuccess$: Observable<Action> = createEffect(() =>
            this.actions$.pipe(
                ofType(AuthenticationActions.logoutUserSuccess),
                tap((action: any) => {

                    const returnUrl = this.router.url;
                    this.router.navigate(['login'], {state: {returnUrl}});
                }),
                tap((action: any) => {
                    window.sessionStorage.clear();
                })
            ),
        { dispatch: false }
    );

    sessionTimeout$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.sessionTimeout),
            withLatestFrom(this.authenticationSelectors.selectAuthenticate()),
            tap(([action, authenticate]) => {
                if (window.parent !== window.self) {
                    window.parent.postMessage({ reAuthenticatedLegacyUser: true }, '*'); // change it to exact url
                }
            }),
            mergeMap(([action, authenticated]) => {
                const message = 'You have been inactive for a while. Do you wish to remain logged in?';
                const data: any = {
                    isAuthenticatedFromAdmin: authenticated.isAuthenticatedFromAdmin,
                    username: authenticated.username
                };

                const modalRef = window.parent === window.self ?
                    this.modalService.show(BsModalConfirmComponent, createConfirmModalOptions('Session timeout', message, 'Yes', 'No', data)) :
                    null;

                return modalRef !== null ? modalRef.content.onClose : of(null);
            }),
            map((dialogResult: any)  => { // map dialog result into action (map function returns observable to action)
                if (window.parent !== window.self) {
                    return AuthenticationActions.loginUser({
                        returnUrl: this.location.path(),
                        isAuthenticatedFromAdmin: false,
                        username: sessionStorage.getItem('username'),
                        password: sessionStorage.getItem('password'),
                        rememberMe: true,
                        returnUrlPathParams: {},
                        accountAccessToken: undefined
                    });
                }

                if (dialogResult.isConfirmed) {
                    return AuthenticationActions.loginUser({
                        returnUrl: this.location.path(),
                        isAuthenticatedFromAdmin: dialogResult.data.isAuthenticatedFromAdmin,
                        username: dialogResult.data.username,
                        password: localStorage.getItem('password'),
                        rememberMe: JSON.parse(localStorage.getItem('rememberMe')),
                        returnUrlPathParams: {},
                        accountAccessToken: undefined
                    });
                }

                return AuthenticationActions.logoutUser();
            })
        )
    );

    failedRequest$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.failedRequest),
            map(action => action.message),
            switchMap(message => {

                const modalRef = this.modalService
                    .show(BsModalConfirmComponent, createConfirmRetryModalOptions('Temporary Server Error', message));

                return modalRef.content.onClose;
            }),
            map((dialogResult: ConfirmDialogResult)  => { // map dialog result into action (map function returns observable to action)
                if (dialogResult.isConfirmed) {
                    return AuthenticationActions.retryFailedRequest();
                }

                return AuthenticationActions.cancelFailedRequest();
            })
        )
    );

    getLatestPortalVersion$: Observable<Action> = createEffect(() =>
            this.actions$.pipe(
                ofType(AuthenticationActions.getLatestPortalVersion),
                withLatestFrom(this.authenticationSelectors.selectAuthenticate()),
                switchMap(([action, authenticate]) =>
                    this.authenticationService
                        .getLatestPortalVersion(authenticate.viewSessionId)
                        .pipe(
                            map((response: any) =>
                                AuthenticationActions
                                    .getLatestPortalVersionSuccess({
                                        isPortalVersionChanged: response.portalVersion !== authenticate.portalVersion})
                            ),
                            catchError(error =>
                                of(AuthenticationActions.sessionTimeout())
                            )
                        )
                )
            )
    );
}
